import {
  queryOptions,
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query'
import type { UIMessage } from 'ai'
import { useCurrentProfile } from '@/hooks/use-current-profile'
import {
  IRIS_AGENT_ID,
  createIrisClient,
  fetchThreadUiMessages,
  isIrisConfigured,
} from './iris-mastra-client'

export { isIrisConfigured }

export interface IrisThreadMetadata {
  /**
   * Legacy frontend-derived title. New threads leave this unset — Mastra
   * generates `thread.title` server-side after the first exchange — but older
   * threads only have this, so reads fall back to it.
   */
  title?: string
  /** Frontend-set pin flag, surfaces the thread in a dedicated section. */
  pinned?: boolean
  /** Owning org id, stamped on first init. */
  orgId?: string
  /**
   * Owning workspace id, stamped on first init. Memory is scoped to the user
   * (`resourceId = userId`), so this is how the sidebar list stays per-workspace —
   * `listMemoryThreads` filters on it.
   */
  workspaceId?: string
  /**
   * Clone lineage, stamped server-side by Mastra when a thread is branched
   * from another conversation.
   */
  clone?: { sourceThreadId: string; clonedAt?: string }
}

/**
 * A Mastra memory thread, as the Iris sidebar consumes it — the camelCase shape
 * `@mastra/client-js` returns (`createdAt`/`updatedAt` arrive as ISO strings),
 * narrowed to our metadata. Used directly; no adapter to any legacy shape.
 */
export type IrisThread = {
  id: string
  title?: string | null
  metadata?: IrisThreadMetadata | null
  createdAt?: string
  updatedAt?: string
}

export const threadsKey = (workspaceId: string) =>
  ['iris-threads', 'workspace', workspaceId] as const

/**
 * A thread's display title: the server-generated `title` first, then the
 * legacy frontend-derived `metadata.title`. Null while the thread has no
 * title yet (Mastra generates one after the first exchange).
 */
export function pickThreadTitle(thread: IrisThread): string | null {
  const generated = thread.title?.trim()
  if (generated) {
    return generated
  }
  const legacy = thread.metadata?.title?.trim()
  return legacy || null
}

const TITLE_PENDING_WINDOW_MS = 3 * 60 * 1000

/**
 * True while a freshly created thread is still waiting for its server-generated
 * title. The window bounds it so old threads that simply never got a title
 * don't read as perpetually loading.
 */
export function isThreadTitlePending(thread: IrisThread): boolean {
  if (pickThreadTitle(thread)) {
    return false
  }
  const created = thread.createdAt ? new Date(thread.createdAt).getTime() : NaN
  return (
    Number.isFinite(created) && Date.now() - created < TITLE_PENDING_WINDOW_MS
  )
}

/**
 * List the current user's Iris threads for this workspace. Memory is scoped to
 * the user (`resourceId = userId`), so we filter by the `workspaceId` stamped in
 * thread metadata to keep the sidebar per-workspace. Doubles as the connectivity
 * probe (throws when Iris is down → caught by IrisErrorBoundary).
 */
export function useIrisThreads(workspaceId: string) {
  const { userId } = useCurrentProfile()
  return useSuspenseQuery({
    queryKey: threadsKey(workspaceId),
    queryFn: async (): Promise<IrisThread[]> => {
      const client = await createIrisClient(workspaceId)
      const result = await client.listMemoryThreads({
        resourceId: userId,
        metadata: { workspaceId },
        agentId: IRIS_AGENT_ID,
      })
      // listMemoryThreads returns a paginated object: { threads, total, ... }.
      return (result as unknown as { threads?: IrisThread[] })?.threads ?? []
    },
    staleTime: 30 * 1000,
    // Title generation finishes shortly after the first response streams, so
    // poll briefly while any fresh thread is untitled — the sidebar swaps its
    // placeholder for the real title without a manual refresh.
    refetchInterval: (query) =>
      query.state.data?.some(isThreadTitlePending) ? 2000 : false,
  })
}

/** Toggle a thread's `pinned` metadata flag (optimistic; rolls back on error). */
export function usePinIrisThread(workspaceId: string) {
  const queryClient = useQueryClient()
  const { userId } = useCurrentProfile()
  return useMutation({
    mutationFn: async ({
      threadId,
      pinned,
    }: {
      threadId: string
      pinned: boolean
    }) => {
      const client = await createIrisClient(workspaceId)
      const thread = client.getMemoryThread({
        threadId,
        agentId: IRIS_AGENT_ID,
      })
      // Update replaces title + metadata, so fetch current and merge the pin
      // flag in (preserving the title and any other metadata).
      const current = (await thread.get()) as {
        title?: string | null
        metadata?: IrisThreadMetadata | null
      }
      await thread.update({
        title: current.title ?? '',
        metadata: { ...(current.metadata ?? {}), pinned },
        resourceId: userId,
      })
      return { threadId, pinned }
    },
    onMutate: async ({ threadId, pinned }) => {
      const key = threadsKey(workspaceId)
      await queryClient.cancelQueries({ queryKey: key })
      const prev = queryClient.getQueryData<IrisThread[]>(key)
      if (prev) {
        queryClient.setQueryData<IrisThread[]>(
          key,
          prev.map((t) =>
            t.id === threadId
              ? { ...t, metadata: { ...(t.metadata ?? {}), pinned } }
              : t
          )
        )
      }
      return { prev }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(threadsKey(workspaceId), ctx.prev)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: threadsKey(workspaceId) })
    },
  })
}

/** Archive (delete) a thread; optimistically removed from the sidebar, restored on error. */
export function useArchiveIrisThread(workspaceId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (threadId: string) => {
      const client = await createIrisClient(workspaceId)
      await client
        .getMemoryThread({ threadId, agentId: IRIS_AGENT_ID })
        .delete()
      return threadId
    },
    onMutate: async (threadId) => {
      const key = threadsKey(workspaceId)
      await queryClient.cancelQueries({ queryKey: key })
      const prev = queryClient.getQueryData<IrisThread[]>(key)
      if (prev) {
        queryClient.setQueryData<IrisThread[]>(
          key,
          prev.filter((t) => t.id !== threadId)
        )
      }
      return { prev }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(threadsKey(workspaceId), ctx.prev)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: threadsKey(workspaceId) })
    },
  })
}

/**
 * Build a short, sidebar-friendly title from the user's first message.
 * Collapses whitespace, trims to ~80 chars at a word boundary, adds an
 * ellipsis if truncated.
 */
export function deriveThreadTitle(text: string, maxLen = 80): string {
  const cleaned = text.replace(/\s+/g, ' ').trim()
  if (cleaned.length <= maxLen) {
    return cleaned
  }
  const sliced = cleaned.slice(0, maxLen)
  const lastSpace = sliced.lastIndexOf(' ')
  const trimmed = lastSpace > 20 ? sliced.slice(0, lastSpace) : sliced
  return `${trimmed.trimEnd()}…`
}

/**
 * Create a thread with its org/workspace metadata up front. The title is left
 * empty on purpose — Mastra's `generateTitle` only names a thread whose title
 * is still blank, and it does so after the first exchange. Called BEFORE the
 * first message streams, so the thread may not exist server-side yet —
 * `createMemoryThread` with our fixed client-minted `threadId` creates it (and
 * races safely alongside the stream, which would otherwise auto-create it).
 * `onMutate` optimistically prepends the thread to the sidebar cache so it
 * appears instantly, with no round-trip; `onSettled` reconciles against the
 * server and `onError` rolls the optimistic row back. Fire-and-forget from
 * callers.
 */
export function useInitIrisThreadMetadata(workspaceId: string) {
  const queryClient = useQueryClient()
  const { userId } = useCurrentProfile()
  return useMutation({
    mutationFn: async ({
      threadId,
      orgId,
    }: {
      threadId: string
      orgId: string
    }) => {
      const client = await createIrisClient(workspaceId)
      const metadata: IrisThreadMetadata = { orgId, workspaceId }
      await client.createMemoryThread({
        threadId,
        agentId: IRIS_AGENT_ID,
        title: '',
        metadata: metadata as Record<string, unknown>,
        resourceId: userId,
      })
      return { threadId }
    },
    // Show the new thread in the sidebar immediately (mirrors usePinIrisThread).
    onMutate: async ({ threadId, orgId }) => {
      const key = threadsKey(workspaceId)
      await queryClient.cancelQueries({ queryKey: key })
      const prev = queryClient.getQueryData<IrisThread[]>(key)
      // `createdAt` buckets the row under "Today" and marks its title pending.
      const optimistic: IrisThread = {
        id: threadId,
        metadata: { orgId, workspaceId },
        createdAt: new Date().toISOString(),
      }
      // Skip if already present (e.g. a retry) so we don't duplicate the row.
      if (prev && !prev.some((t) => t.id === threadId)) {
        queryClient.setQueryData<IrisThread[]>(key, [optimistic, ...prev])
      }
      return { prev }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(threadsKey(workspaceId), ctx.prev)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: threadsKey(workspaceId) })
    },
  })
}

/**
 * A thread's stored history as AI SDK `UIMessage[]`, ready to seed its `Chat`.
 * The single source of truth for the `['iris-thread-messages', id]` cache shared
 * by the chat view, the route loader, and the sidebar's viewport preload — so
 * opening a thread you've already scrolled past is instant.
 */
export const irisThreadMessages = {
  findOne: (threadId: string, workspaceId: string) =>
    queryOptions({
      queryKey: ['iris-thread-messages', threadId] as const,
      queryFn: (): Promise<UIMessage[]> =>
        fetchThreadUiMessages(threadId, workspaceId),
      staleTime: 30 * 1000,
    }),
}

/**
 * Whether this thread is the target of an active schedule — i.e. whether it can
 * ever receive out-of-band (scheduled) messages. Gates live-update polling so
 * ordinary conversations, which never get background writes, don't poll at all.
 */
export const irisThreadSchedule = {
  findOneByThread: (threadId: string, userId: string, workspaceId: string) =>
    queryOptions({
      queryKey: ['iris-thread-schedule', threadId] as const,
      queryFn: async (): Promise<boolean> => {
        const client = await createIrisClient(workspaceId)
        const { schedules } = await client.listSchedules({
          agentId: IRIS_AGENT_ID,
          threadId,
          resourceId: userId,
        })
        return schedules.some((s) => s.status === 'active')
      },
      staleTime: 60 * 1000,
    }),
}
